/**
 * PURPOSE: Normalizar UNA vez las filas del Centro de Programación para las cuatro vistas (FTR-0028
 *   WG3, D-23).
 * RESPONSIBILITIES: Convertir la fila cruda de `GET /draft` en un modelo de vista estable
 *   (`ProgrammingMatchViewModel`) con etiquetas de referencia, participantes resueltos, estado y
 *   capacidades. Puro: sin red, sin React, sin fechas del sistema.
 *
 *   REGLA DE VERDAD (D-08). `GET /draft` YA devuelve las filas materializadas hidratadas desde
 *   `matches`: `date_match`, `time_match`, `idsede` e `idcancha` vienen del partido y el bloque
 *   `match` trae participantes, estado y `participantsPending`. Aquí NO se vuelve a elegir entre dos
 *   fuentes: se lee lo que el servidor ya resolvió. Las columnas de programación de la fila solo
 *   mandan mientras no hay `idmatch`, que es justo lo que el backend ya aplicó.
 *
 *   C9 — LA JORNADA ES POR PLANTILLA, NO GLOBAL. `date_no` pertenece al tamaño del grupo: con
 *   `group_sizes = [5, 4]` la fecha 4 es ronda 1 para el grupo de 5 y ronda 2 para el de 4. Por eso
 *   el modelo expone `leg` junto a `dateNo` y nunca los equipara: la interfaz muestra ambos y no
 *   inventa una equivalencia entre número de fecha y ronda.
 * COLLABORATORS: programmingCenterGrouping.js, programmingCenterEdit.js, las cuatro vistas y
 *   MatchRow.jsx. Espeja la gramática de `engine/referenceGrammar.js` del backend.
 * ROUTE: winscore/src/utils/programmingCenterModel.js
 */

/** 1 → "A" … 10 → "J". Mismo mapeo que `groupLetter` del backend y que `grupos.nombre_grupo`. */
export const groupLetter = (group) => {
  const parsed = Number(group);
  if (!Number.isInteger(parsed) || parsed < 1 || parsed > 26) return "";
  return String.fromCharCode(64 + parsed);
};

const GROUP_REF = /^(SLOT|POS):G(\d{1,2}):(\d{1,3})$/;
const ACUM_REF = /^ACUM:(\d{1,3})$/;
const MATCH_REF = /^(WIN|LOSS):(.+)$/;
const PART_REF = /^PART:(\d{1,10})$/;
const TXT_REF = /^TXT:(.+)$/;

/** `F-QF-1` → `QF1`; una clave de grupo se deja tal cual. Igual que `shortMatchLabel` del backend. */
export const shortMatchLabel = (key) => {
  const clean = String(key || "").replace(/^PC-/, "");
  const final = /^F-([A-Z0-9]+)-(\d+)$/.exec(clean);
  return final ? `${final[1]}${final[2]}` : clean;
};

/**
 * Lectura de una referencia estructural. Espeja `parseReference` del backend PERO nunca rechaza: una
 * referencia que no entiende se muestra literal en vez de romper una vista. La validación es del
 * servidor, que es quien puede decir `REFERENCE_INVALID`.
 */
export const parseReference = (raw) => {
  const text = String(raw || "").trim();
  if (!text) return null;

  const group = GROUP_REF.exec(text);
  if (group) {
    return {
      type: group[1],
      group: Number(group[2]),
      ...(group[1] === "SLOT" ? { slot: Number(group[3]) } : { position: Number(group[3]) }),
    };
  }
  const acum = ACUM_REF.exec(text);
  if (acum) return { type: "ACUM", position: Number(acum[1]) };

  const match = MATCH_REF.exec(text);
  if (match) return { type: match[1], matchKey: match[2] };

  const part = PART_REF.exec(text);
  if (part) return { type: "PART", idparticipant: Number(part[1]) };

  const txt = TXT_REF.exec(text);
  if (txt) return { type: "TXT", label: txt[1] };

  return { type: "RAW", label: text };
};

/** Etiqueta legible de una referencia: "A4", "1.º B", "5.º acumulado", "Ganador QF1". */
export const referenceLabel = (raw) => {
  const parsed = typeof raw === "string" || raw === null || raw === undefined ? parseReference(raw) : raw;
  if (!parsed) return "";
  switch (parsed.type) {
    case "SLOT":
      return `${groupLetter(parsed.group)}${parsed.slot}`;
    case "POS":
      return `${parsed.position}.º ${groupLetter(parsed.group)}`;
    case "ACUM":
      return `${parsed.position}.º acumulado`;
    case "WIN":
      return `Ganador ${shortMatchLabel(parsed.matchKey)}`;
    case "LOSS":
      return `Perdedor ${shortMatchLabel(parsed.matchKey)}`;
    case "PART":
      return `Participante ${parsed.idparticipant}`;
    case "TXT":
    case "RAW":
      return parsed.label;
    default:
      return "";
  }
};

/** Nombre visible de una institución ya sorteada. Nunca se muestra un id técnico (§17). */
export const participantLabel = (participant) => {
  if (!participant) return null;
  return (
    participant.institution?.abrev ||
    participant.institution?.name ||
    participant.displayName ||
    null
  );
};

export const PENDING_PARTICIPANT_LABEL = "A definir";

/** Un lado de fase final todavía sin cruce. Nace así a propósito (D-26): no es un dato que falte. */
export const UNDEFINED_SIDE_LABEL = "Por definir";

export const ROW_STATUS = Object.freeze({
  DRAFT: "draft",
  MATERIALIZED: "materialized",
  OFFICIALIZED: "officialized",
  ORPHAN: "orphan",
});

/** Estados de partido de `matches.state` que el Centro necesita distinguir. */
const MATCH_STATE_OFFICIALIZED = 4;
const MATCH_STATE_DELETED = -2;

/** Índices que todas las vistas comparten, construidos una sola vez desde `GET /context`. */
export const buildContextIndex = (context) => {
  const participants = Array.isArray(context?.participants) ? context.participants : [];
  const venues = Array.isArray(context?.venues) ? context.venues : [];

  const participantsById = new Map();
  const participantsBySlot = new Map();
  for (const participant of participants) {
    if (participant?.idparticipant) participantsById.set(Number(participant.idparticipant), participant);
    if (participant?.idgrupo && participant?.nro_grupo) {
      // Un slot con dos participantes activos es AMBIGUO: el backend lo deja sin resolver y lo
      // reporta. Aquí se refleja igual — no se elige uno por orden de llegada.
      const key = `${Number(participant.idgrupo)}:${Number(participant.nro_grupo)}`;
      const current = participantsBySlot.get(key);
      participantsBySlot.set(key, current ? { ...current, ambiguous: true } : { ...participant });
    }
  }

  const venuesById = new Map();
  const courtsById = new Map();
  for (const venue of venues) {
    if (!venue?.idsede) continue;
    venuesById.set(Number(venue.idsede), venue);
    for (const court of venue.courts || []) {
      if (court?.idcancha) courtsById.set(Number(court.idcancha), { ...court, venue });
    }
  }

  return { participantsById, participantsBySlot, venuesById, courtsById };
};

const sideFromRow = ({ reference, idparticipant, index, row, matchBlock }) => {
  const parsed = parseReference(reference);
  const label = referenceLabel(parsed);
  const participant = idparticipant || null;
  return {
    index,
    reference: reference || null,
    referenceType: parsed?.type || null,
    slotLabel: label || null,
    idparticipant: participant,
    // `participantLabel` se rellena fuera, con el índice del contexto.
    participantName: null,
    // Un lado sin participante SOLO está pendiente si la fila ya es un partido real: antes de
    // materializar, "sin participante" es lo normal y no un aviso.
    pending: Boolean(row.idmatch) && !participant && !(row.kind === "bye" && index === 2),
    matchBlock,
  };
};

/**
 * Modelo de vista de UNA fila. Es lo que consumen las cuatro vistas, la barra de acciones y las
 * validaciones: un solo objeto, calculado una vez.
 */
export const buildRowViewModel = (row, index = {}) => {
  const { participantsById = new Map(), venuesById = new Map(), courtsById = new Map() } = index;
  const match = row?.match || null;
  const isBye = row?.kind === "bye";
  const isOrphan = row?.row_status === "orphan";
  const isMaterialized = Boolean(row?.idmatch);
  const isDeleted = Boolean(match?.deleted) || Number(match?.state) === MATCH_STATE_DELETED;
  const isOfficialized = Number(match?.state) === MATCH_STATE_OFFICIALIZED;

  const homeReference = isBye ? row.bye_ref || row.home_ref : row.home_ref;
  const home = sideFromRow({
    reference: homeReference,
    idparticipant: isBye ? match?.idparticipant_bye || match?.idparticipant1 : match?.idparticipant1,
    index: 1,
    row,
    matchBlock: match,
  });
  const away = isBye
    ? null
    : sideFromRow({
        reference: row.away_ref,
        idparticipant: match?.idparticipant2,
        index: 2,
        row,
        matchBlock: match,
      });

  for (const side of [home, away]) {
    if (!side || !side.idparticipant) continue;
    side.participantName = participantLabel(participantsById.get(Number(side.idparticipant)));
  }

  const venue = row?.idsede ? venuesById.get(Number(row.idsede)) || null : null;
  const court = row?.idcancha ? courtsById.get(Number(row.idcancha)) || null : null;

  // Un BYE no se programa NUNCA (D-20): no está "sin programar", está fuera del calendario.
  const isScheduled = !isBye && Boolean(row?.date_match && row?.time_match);

  let status = ROW_STATUS.DRAFT;
  if (isOrphan) status = ROW_STATUS.ORPHAN;
  else if (isOfficialized) status = ROW_STATUS.OFFICIALIZED;
  else if (isMaterialized) status = ROW_STATUS.MATERIALIZED;

  return {
    key: row?.draft_key || `row-${row?.iddraftmatch}`,
    iddraftmatch: row?.iddraftmatch ?? null,
    draftKey: row?.draft_key || null,
    kind: row?.kind || null,
    idtypephase: row?.idtypephase ?? null,
    groupNo: row?.group_no ?? null,
    groupLabel: row?.group_no ? `Grupo ${groupLetter(row.group_no)}` : null,
    leg: row?.leg ?? null,
    // Cuántas rondas tiene REALMENTE el grupo de esta fila. Lo rellena `buildRowViewModels`, que es
    // quien ve el conjunto completo: una fila aislada no puede saberlo.
    legCount: null,
    dateNo: row?.date_no ?? null,
    matchNo: row?.match_no ?? null,
    stage: row?.stage || null,
    stagePos: row?.stage_pos ?? null,
    tagFinal: row?.tag_final || null,
    sortOrder: row?.sort_order ?? 0,

    home,
    away,
    // La primera etapa de una fase final nace sin referencias (D-26): se nombra "Por definir", no
    // "null". No es un hueco: es el estado correcto de una llave cuyos clasificados no existen aún.
    label: away
      ? `${home.slotLabel || UNDEFINED_SIDE_LABEL} vs ${away.slotLabel || UNDEFINED_SIDE_LABEL}`
      : `${home.slotLabel || UNDEFINED_SIDE_LABEL} descansa`,

    date: row?.date_match || null,
    time: row?.time_match || null,
    idsede: row?.idsede ?? null,
    idcancha: row?.idcancha ?? null,
    venueName: venue?.name || null,
    courtName: court?.name || null,
    courtRetired: court ? Number(court.state) !== 1 : false,

    idmatch: row?.idmatch ?? null,
    nroMatch: match?.nro_match ?? null,
    matchKey: match?.match_key || null,
    matchState: match ? Number(match.state) : null,
    published: Boolean(match?.published),
    participantsPending: Boolean(match?.participantsPending),

    status,
    isBye,
    isOrphan,
    isMaterialized,
    isDeleted,
    isOfficialized,
    isScheduled,
    isManual: row?.kind === "manual",
    isFinal: row?.kind === "final",
    isLocked: Boolean(row?.locked),

    // Capacidades. Son una pista de interfaz — el backend vuelve a decidir — pero evitan ofrecer una
    // acción que se sabe que va a ser rechazada (§9: "no mostrar acciones que backend rechazaría").
    canSchedule: !isBye && !isOrphan && !isDeleted,
    canSelect: !isBye && !isOrphan && !isDeleted,
    canDeleteRow: !isMaterialized && !isOrphan,
    canRetireMatch: isMaterialized && !isOfficialized && !isDeleted,
  };
};

/** Todas las filas, ya normalizadas y ordenadas por el orden canónico del backend. */
export const buildRowViewModels = (rows = [], index = {}) => {
  const models = (Array.isArray(rows) ? rows : []).map((row) => buildRowViewModel(row, index));

  // Las rondas de un grupo se cuentan sobre el CONJUNTO, nunca sobre lo que un filtro deja visible:
  // si una pantalla filtrada oculta la vuelta, la clave no debe dejar de decir que hay ida y vuelta.
  const legsByGroup = new Map();
  for (const model of models) {
    if (!model.groupNo || !model.leg) continue;
    const key = Number(model.groupNo);
    if (!legsByGroup.has(key)) legsByGroup.set(key, new Set());
    legsByGroup.get(key).add(Number(model.leg));
  }
  for (const model of models) {
    model.legCount = model.groupNo ? legsByGroup.get(Number(model.groupNo))?.size || 0 : 0;
  }

  return models.sort(
    (a, b) =>
      a.sortOrder - b.sortOrder ||
      String(a.draftKey || "").localeCompare(String(b.draftKey || ""))
  );
};

/**
 * Etiqueta de la jornada. Se nombra la fecha SIN afirmar que equivale a una ronda, porque con grupos
 * de distinto tamaño no lo es (C9). La ronda se muestra aparte, por fila.
 */
export const dateLabel = (dateNo, { idtypephase } = {}) => {
  if (Number(idtypephase) === 2) return `Etapa ${dateNo}`;
  return `Fecha ${dateNo}`;
};

export const legLabel = (leg) => {
  const parsed = Number(leg);
  if (!Number.isInteger(parsed) || parsed < 1) return null;
  return parsed === 1 ? "Ida" : parsed === 2 ? "Vuelta" : `Ronda ${parsed}`;
};

/** `G1-L1-1v3` → grupo 1, ronda 1, cruce `1v3`. Las claves finales y manuales no encajan aquí. */
const GROUP_DRAFT_KEY = /^G(\d{1,2})-L(\d{1,2})-(.+)$/;

/**
 * Cómo se LEE la clave de una fila. La identidad interna —`draft_key`, `match_key`, `codigo_match`—
 * no cambia nunca: lo único que cambia aquí es su representación.
 *
 * `L1` no informa de nada cuando el grupo tiene una sola ronda, así que no se muestra: obliga al
 * operador a interpretar una distinción que no existe. Con dos rondas sí se muestra, acompañada de
 * "Ida"/"Vuelta" para que no tenga que traducir `L1`/`L2` de memoria. Una ronda 2 o posterior se
 * nombra siempre, incluso si el conjunto disponible no permite contarlas.
 */
export const displayMatchKey = (row) => {
  const raw = String(row?.draftKey || "");
  const parsed = GROUP_DRAFT_KEY.exec(raw);
  if (!parsed) {
    const short = shortMatchLabel(raw);
    return { text: short, parts: short ? [short] : [], title: raw };
  }

  const leg = Number(parsed[2]);
  const parts = [`G${parsed[1]}`];
  if (leg > 1 || Number(row?.legCount) > 1) {
    const name = legLabel(leg);
    if (name) parts.push(name);
    parts.push(`L${leg}`);
  }
  parts.push(parsed[3]);
  return { text: parts.join(" · "), parts, title: raw };
};

/** Nombre visible de cada lado: la institución sorteada, o el slot con "A definir". */
export const sideDisplay = (side) => {
  if (!side) return { primary: "", secondary: null, pending: false };
  if (side.participantName) {
    return { primary: side.participantName, secondary: side.slotLabel, pending: false };
  }
  return {
    primary: side.slotLabel || UNDEFINED_SIDE_LABEL,
    secondary: side.pending ? PENDING_PARTICIPANT_LABEL : null,
    pending: side.pending,
  };
};

/** Recuentos que la cabecera muestra sin recorrer las filas en cada render. */
export const summarizeRows = (viewModels = []) => {
  const summary = {
    total: viewModels.length,
    active: 0,
    orphan: 0,
    byes: 0,
    materialized: 0,
    scheduled: 0,
    unscheduled: 0,
    officialized: 0,
    participantsPending: 0,
    finals: 0,
    manual: 0,
  };
  for (const row of viewModels) {
    if (row.isOrphan) summary.orphan += 1;
    else summary.active += 1;
    if (row.isBye) summary.byes += 1;
    if (row.isMaterialized) summary.materialized += 1;
    if (row.isOfficialized) summary.officialized += 1;
    if (row.isFinal) summary.finals += 1;
    if (row.isManual) summary.manual += 1;
    if (row.participantsPending) summary.participantsPending += 1;
    if (!row.isBye && !row.isOrphan) {
      if (row.isScheduled) summary.scheduled += 1;
      else summary.unscheduled += 1;
    }
  }
  return summary;
};
